import { useEffect, useRef, useState } from "react";
import SimpleReactValidator from "simple-react-validator";
import AppService from "../../../services/app.service";
import Loader from "../../../components/loader";
import GenericMultiSelectField from "../../../components/GenericMultiSelectField";
import { CustomDatePicker } from "../../../components/CustomDatePicker";
import {
  getMultiSelectOptions,
  handleMultiSelectChange,
  listWithInActiveElements,
  selectAllAccounts,
} from "../../../utils/utils";
import Layout from "../../../layout/Layout";
import TipTapEditor from "../../../components/TipTapEditor";
import { MessageService } from "../../../services/MessageService";
import { showErrorMessage } from "../../../components/ShowFeildValidationError";
import { expiry_date_message } from "../../../utils/constants";
import moment from "moment";

const CreateOrUpdateMessage = (props) => {
  const [formState, setFormState] = useState({
    label: "",
    description: "",
    content: null,
    clientViewer: [],
    startDate: moment(new Date()).utc().format("MM-DD-YYYY"),
    expiryDate: "",
    active: true,
    loading: false,
    accountsList: [],
  });
  const [error, setError] = useState({
    messageContent: "",
    expiry: "",
    isExpiryValid: true,
  });
  const [, forceUpdate] = useState(0);
  const today = moment(new Date()).utc().format("MM-DD-YYYY");

  const simpleValidator = useRef(
    new SimpleReactValidator({ autoForceUpdate: { forceUpdate: forceUpdate } })
  );

  const [isEdit, setIsEdit] = useState(
    !props["match"].params.messageId
      ? { title: "Add", submitButtonTitle: "Create" }
      : {
          messageId: props["match"].params.messageId,
          title: "Edit",
          submitButtonTitle: "Update",
        }
  );

  const updateFormState = (updates) => {
    setFormState((prevState) => ({ ...prevState, ...updates }));
  };

  const loadData = async () => {
    updateFormState({ loading: true });
    let message = {};
    if (isEdit.messageId) {
      const messageData = await MessageService.getMessageById(isEdit.messageId);
      updateFormState({
        ...messageData,
        expiryDate:
          messageData.endDate !== null || messageData.endDate != ""
            ? moment(messageData?.endDate).utc().format("MM-DD-YYYY")
            : "",
      });
    }
    const accounts = await AppService.getAll("accounts/activeAccounts");
    const accountList = accounts.data.map((account) => ({
      value: account.accId,
      label: account.accName,
    }));
    updateFormState({
      accountsList: listWithInActiveElements(
        accountList,
        message?.clientViewer
      ),
      loading: false,
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleChange = (event) => {
    updateFormState({ [event.target.name]: event.target.value });
  };

  const handleCheckBox = (event) => {
    updateFormState({ [event.target.name]: event.target.checked });
  };

  const handleEditorChange = (value) => {
    setError((prevState) => ({ ...prevState, messageContent: "" }));
    updateFormState({ content: value });
  };

  const handleMultiAccountChange = (value, action) => {
    const list = handleMultiSelectChange(
      value,
      action,
      formState.accountsList,
      formState.clientViewer,
      selectAllAccounts
    );
    updateFormState({ clientViewer: list });
  };

  const handleStartDateChange = (dateInfo) => {
    const value = dateInfo?.date || "";
    setError((prev) => ({ ...prev, expiry: "" }));
    updateFormState({ startDate: value });

    // if field is empty → required must show
    if (!value) {
      simpleValidator.current.showMessageFor("Start Date");
      return;
    }
    // if user typed or selected something → hide required
    simpleValidator.current.hideMessageFor("Start Date");
  };

  const handleExpiryDateChange = (expiry) => {
    setError((prev) => ({
      ...prev,
      expiry: "",
      isExpiryValid: true,
    }));

    const value = expiry?.date || "";

    const expiryDate = moment(value).startOf("day");
    const startDate = moment(formState.startDate).startOf("day");

    // expiry > today AND expiry < startDate:
    if (expiryDate.isBefore(startDate) && expiryDate.isSameOrAfter(today)) {
      setError((prev) => ({
        ...prev,
        expiry: expiry_date_message,
        isExpiryValid: true,
      }));
    } else {
      setError((prev) => ({
        ...prev,
        isExpiryValid: expiry.isValid,
      }));
    }

    updateFormState({ expiryDate: value });

    if (expiryDate.isBefore(today)) {
      setError((prev) => ({
        ...prev,
        isExpiryValid: false,
      }));
    }
  };

  const cleanHtml = (html) => {
    if (!html) return "";

    return (
      html
        .trim()
        // Remove empty <p> tags (including with <br>, &nbsp;, or whitespace)
        .replace(/<p[^>]*>\s*(?:<br\s*\/?\s*>*|&nbsp;|\s)*\s*<\/p>/gi, "")
        // Remove any stray <br> at start/end
        .replace(/^(<br\s*\/?>)+|(<br\s*\/?>)+$/gi, "")
        .trim()
    );
  };

  const submitForm = async (e) => {
    e.preventDefault();

    let cleaned = cleanHtml(formState.content);

    if (cleaned === "") {
      setError((prevState) => ({
        ...prevState,
        messageContent: "Message Content is required.",
      }));
    }

    const isExpiryLessThanStartDate = moment(formState.expiryDate)
      .startOf("day")
      .isBefore(moment(formState.startDate).startOf("day"));
    if (
      simpleValidator.current.allValid() &&
      cleaned !== "" &&
      !(formState.expiryDate && isExpiryLessThanStartDate) &&
      formState.startDate >= today &&
      error.expiry === "" &&
      error.isExpiryValid
    ) {
      updateFormState({ loading: true });
      let result;
      const messageData = {
        id: isEdit.messageId,
        title: formState.label.trim(),
        description: formState.description.trim(),
        active: formState.active,
        clientViewer: formState.clientViewer,
        startDate: new Date(moment.utc(formState.startDate).toISOString()),
        endDate:
          formState?.expiryDate !== null &&
          formState?.expiryDate !== "" &&
          formState?.expiryDate !== "Invalid date" && formState?.expiryDate !== "Invalid Date" 
            ? new Date(moment.utc(formState.expiryDate).toISOString())
            : "",
        content: cleaned,
      };

      if (isEdit.messageId) {
        result = await MessageService.updateMessageById(
          isEdit.messageId,
          messageData
        );
      } else {
        result = await MessageService.saveMessage(messageData, "messages");
      }

      updateFormState({ loading: false });
      if (result.status === 200) {
        props.history.push("/messages");
      }
    } else {
      if (
        formState.expiryDate !== "" &&
        isExpiryLessThanStartDate &&
        error.isExpiryValid
      ) {
        setError((prev) => ({ ...prev, expiry: expiry_date_message }));
      }
      simpleValidator.current.showMessages();
    }
  };

  const discardForm = (e) => {
    props.history.push("/messages");
  };

  return (
    <div>
      <Layout
        title="Message Center"
        history={props.history}
        titleUrl="/messages"
        subTitle={`${isEdit.title} Message`}
        activeClientLoginMessagesLink={true}
        showBreadCrumb={true}
      >
        <section className="forms">
          <div className="container-fluid">
            <div className="row">
              {/* <!-- Horizontal Form--> */}
              <div className="col-lg-12">
                <div className="card">
                  <div className="card-header d-flex align-items-center">
                    <h3 className="sub-heading">{isEdit.title} Message</h3>
                  </div>
                  <div className="card-body">
                    <form className="form-horizontal" noValidate={true}>
                      <div className="form-group row">
                        <label className="col-sm-3 form-control-label required label">
                          Title
                        </label>
                        <div className="col-sm-9">
                          <input
                            name="label"
                            type="text"
                            maxLength={500}
                            placeholder="Message Title"
                            className="form-control form-control-success fields-font"
                            value={formState.label}
                            onChange={(event) => handleChange(event)}
                            readOnly={isEdit.messageId}
                          />
                          {simpleValidator.current.message(
                            "title",
                            formState.label,
                            "required"
                          )}
                        </div>
                      </div>
                      <div className="form-group row">
                        <label className="col-sm-3 form-control-label required label">
                          Description
                        </label>
                        <div className="col-sm-9">
                          <textarea
                            name="description"
                            rows={3}
                            maxLength={5000}
                            placeholder="Description"
                            className="form-control form-control-success fields-font"
                            value={formState.description}
                            onChange={(event) => handleChange(event)}
                          />
                          {simpleValidator.current.message(
                            "description",
                            formState.description,
                            "required"
                          )}
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-sm-3 form-control-label required label">
                          Message Content
                        </label>
                        <div className="col-sm-9 email-editor fields-font">
                          {isEdit.title === "Add" && (
                            <TipTapEditor
                              content={formState.content}
                              onChange={handleEditorChange}
                            />
                          )}
                          {formState.content && isEdit.title !== "Add" && (
                            <TipTapEditor
                              content={formState.content}
                              onChange={handleEditorChange}
                            />
                          )}
                        </div>
                        {error.messageContent && (
                          <div className="col-9 offset-3 mt-1">
                            <p className="msg-error m-0 p-0">
                              {error.messageContent}
                            </p>
                          </div>
                        )}
                      </div>

                      <GenericMultiSelectField
                        label="Select Client Viewer"
                        name="clientViewer"
                        isMulti={true}
                        options={getMultiSelectOptions(
                          formState.accountsList,
                          formState.clientViewer,
                          selectAllAccounts
                        )}
                        value={formState.clientViewer}
                        onChange={handleMultiAccountChange}
                        errorMessage={simpleValidator.current.message(
                          "Client Viewer",
                          formState.clientViewer,
                          "required"
                        )}
                        required={true}
                      />

                      <div className="form-group row">
                        <label className="col-sm-3 form-control-label required label">
                          Start Date
                        </label>
                        <div className="col-sm-9">
                          <div
                            className="form-control fields-font"
                            style={{
                              padding: 0,
                              paddingTop: 4,
                              paddingLeft: 8,
                            }}
                          >
                            <CustomDatePicker
                              expiryDate={formState.startDate}
                              handleDateChange={(dateInfo) =>
                                handleStartDateChange(dateInfo)
                              }
                              disablePast={true}
                            />
                          </div>
                          {simpleValidator.current.message(
                            "Start Date",
                            formState.startDate,
                            "required"
                          )}
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-sm-3 form-control-label label">
                          Expiry Date
                        </label>
                        <div className="col-sm-9">
                          <div
                            className="form-control fields-font"
                            style={{
                              padding: 0,
                              paddingTop: 4,
                              paddingLeft: 8,
                            }}
                          >
                            <CustomDatePicker
                              expiryDate={formState.expiryDate}
                              handleDateChange={(dateInfo) =>
                                handleExpiryDateChange(dateInfo)
                              }
                              disablePast={true}
                            />
                          </div>
                        </div>
                        {error.expiry !== "" && showErrorMessage(error.expiry)}
                      </div>

                      <div className="form-group row">
                        <div className="col-sm-3 form-control-label"></div>
                        <div className="col-sm-9">
                          <div className="custom-control custom-checkbox checkbox-xl">
                            <input
                              id="active"
                              type="checkbox"
                              checked={formState.active}
                              name="active"
                              className="custom-control-input"
                              onChange={(event) => handleCheckBox(event)}
                            />
                            <label
                              className="custom-control-label"
                              htmlFor="active"
                            >
                              Active
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="form-group row">
                        <div className="col-sm-9 offset-sm-3">
                          <button
                            type="submit"
                            onClick={submitForm}
                            className="btn btn-submit float-right buttons"
                          >
                            {isEdit.submitButtonTitle} Message
                          </button>
                          <button
                            type="button"
                            className="btn btn-cancel float-right buttons"
                            onClick={discardForm}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
      {formState.loading && (
        <div className="overlay">
          <Loader loading={formState.loading} />
        </div>
      )}
    </div>
  );
};

export default CreateOrUpdateMessage;
